import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Search, AlertCircle, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ShieldAlert, ShieldCheck } from 'lucide-react';
import { dataQualityApi } from '../services/dataQualityApi';
import './DataQualityListPage.css';

const CHECK_META: Record<string, { label: string; description: string }> = {
  missing_address: { label: 'Missing Address', description: 'Clinics missing street, city, or state in their mailing address.' },
  incomplete_address: { label: 'Incomplete Address', description: 'Clinics with a street address but missing state or zip code.' },
  not_geocoded: { label: 'Not Geocoded', description: 'Clinics with a complete address that have not been geocoded yet.' },
  geocode_failed: { label: 'Geocoding Failed', description: 'Clinics where geocoding was attempted but failed. Address may need correction.' },
  no_shipping_address: { label: 'No Shipping Address', description: 'Clinics without a shipping address for kit delivery.' },
  no_phone: { label: 'No Phone Number', description: 'Clinics without a main phone number on file.' },
  no_email: { label: 'No Email Address', description: 'Clinics without an email address on file.' },
  no_phone_or_email: { label: 'No Phone AND No Email', description: 'Clinics with neither phone nor email — completely unreachable.' },
  no_contacts: { label: 'No Contacts', description: 'Clinics with no named contact persons in the system.' },
  no_services: { label: 'No Services Configured', description: 'Clinics with no services or procedures configured for pricing.' },
  expired_fees: { label: 'All Fees Expired', description: 'Clinics where all configured service fees have expired.' },
  no_hours: { label: 'No Operating Hours', description: 'Clinics with no operating hours specified for any day of the week.' },
  unknown_capabilities: { label: 'Unknown Capabilities', description: 'Clinics with no capability information (PAT, X-Ray, Fit Testing).' },
  no_w9: { label: 'No W-9 On File', description: 'Active clinics without a W-9 tax form on file.' },
  no_appointment_method: { label: 'No Appointment Method', description: 'Clinics without a preferred method of appointment scheduling.' },
  no_reporting_method: { label: 'No Reporting Method', description: 'Clinics without a result reporting method specified.' },
};

export default function DataQualityListPage() {
  const { checkId } = useParams<{ checkId: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  const pageSize = 20;

  const meta = checkId ? CHECK_META[checkId] : null;

  const { data, isLoading, error } = useQuery({
    queryKey: ['data-quality-clinics', checkId, page, pageSize, search, sortColumn, sortDirection],
    queryFn: () => dataQualityApi.getClinicsByCheck(checkId!, page, pageSize, search || undefined, sortColumn || undefined, sortDirection || undefined),
    enabled: !!checkId,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else {
        setSortColumn(null);
        setSortDirection('ASC');
      }
    } else {
      setSortColumn(column);
      setSortDirection('ASC');
    }
    setPage(1);
  };

  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'ASC' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };


  if (!checkId || (!meta && !isLoading)) {
    return (
      <div className="dq-list-page">
        <div className="dq-list-breadcrumb">
          <Link to="/data-quality">Data Quality</Link>
          <ChevronRight size={14} />
          <span>{checkId || 'Unknown'}</span>
        </div>
        <div className="dq-list-empty">
          <AlertCircle size={48} />
          <h3>Check not found</h3>
          <p>The data quality check "{checkId}" does not exist.</p>
          <Link to="/data-quality" className="btn-search">
            Back to Data Quality
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="dq-list-page">
      <div className="dq-list-breadcrumb">
        <Link to="/data-quality">Data Quality</Link>
        <ChevronRight size={14} />
        <span>{meta?.label || checkId}</span>
      </div>

      <div className="page-header">
        <div className="page-title-row">
          <ShieldAlert size={28} />
          <h1>{meta?.label || 'Data Quality Check'}</h1>
        </div>
        <p className="page-subtitle">{meta?.description || 'Clinics with data quality issues.'}</p>
      </div>

      <div className="dq-list-toolbar">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search by clinic name or code..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search clinics"
            />
            {searchInput && (
              <button type="button" className="clear-search" onClick={handleClearSearch}>
                &times;
              </button>
            )}
          </div>
          <button type="submit" className="btn-search">Search</button>
        </form>

        {data && data.totalCount > 0 && (
          <div className="dq-list-summary">
            <span className="total-count">{data.totalCount.toLocaleString()}</span> clinic{data.totalCount !== 1 ? 's' : ''} affected
          </div>
        )}
      </div>

      {isLoading && (
        <div className="dq-list-loading">
          <div className="spinner" />
          <p>Loading clinics...</p>
        </div>
      )}

      {error && (
        <div className="dq-list-error">
          <AlertCircle size={24} />
          <p>Failed to load clinics. Please try again.</p>
        </div>
      )}

      {data && data.items.length === 0 && (
        data.totalCount === 0 ? (
          <div className="dq-list-empty dq-list-empty-success">
            <ShieldCheck size={48} />
            <h3>All clinics pass this check</h3>
            <p>No clinics currently have this data quality issue.</p>
          </div>
        ) : (
          <div className="dq-list-empty">
            <Search size={48} />
            <h3>No matching clinics found</h3>
            <p>{search ? 'Try a different search term.' : 'Try a different page.'}</p>
          </div>
        )
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="dq-list-table-container">
            <table className="dq-list-table">
              <thead>
                <tr>
                  <th className="col-sortable" onClick={() => handleSort('Name')}>
                    Clinic Name {renderSortIcon('Name')}
                  </th>
                  <th className="col-sortable" onClick={() => handleSort('Code')}>
                    Code {renderSortIcon('Code')}
                  </th>
                  <th className="col-sortable" onClick={() => handleSort('City')}>
                    City {renderSortIcon('City')}
                  </th>
                  <th className="col-sortable" onClick={() => handleSort('State')}>
                    State {renderSortIcon('State')}
                  </th>
                  <th className="col-sortable" onClick={() => handleSort('Phone')}>
                    Phone {renderSortIcon('Phone')}
                  </th>
                  <th>Status</th>
                  <th>Issue Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => navigate(`/clinics/${item.id}`)}
                  >
                    <td className="col-name">{item.name}</td>
                    <td>{item.code || '-'}</td>
                    <td>{item.city || '-'}</td>
                    <td>{item.state || '-'}</td>
                    <td>{item.phone || '-'}</td>
                    <td>{item.status || '-'}</td>
                    <td className="col-issue">{item.issueDetail || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.totalPages > 1 && (
            <div className="dq-list-pagination">
              <button
                className="pagination-btn"
                disabled={!data.hasPreviousPage}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              <span className="pagination-info">
                Page {data.pageNumber} of {data.totalPages}
              </span>
              <button
                className="pagination-btn"
                disabled={!data.hasNextPage}
                onClick={() => setPage(page + 1)}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
